import { takeLatest, call, put } from 'redux-saga/effects';
import { select } from '@redux-saga/core/effects';
import {
  setLink,
  setLoading,
  setError,
  setLanguage,
  submitAnswers as submitAnswersAction,
  requestLink,
  RequestLinkPayload
} from '.';
import axios from '@utils/axios';
import i18n from '@app/i18n';
import questionnaireService from '@app/services/QuestionnaireService';
import { Form, FormValue, Patient, Question, QuestionnaireSelectorState, Task } from '@app/types';
import { SagaIterator } from 'redux-saga';
import { push } from 'react-router-redux';

interface RequestAction {
  type: typeof requestLink.type;
  payload: RequestLinkPayload;
}

interface SubmitAnswersAction {
  type: typeof submitAnswersAction.type;
  payload?: { incomplete?: boolean };
}

interface ApiResponse {
  task: Task;
  patient: Patient;
  form: Form;
}

function* request(action: { payload: RequestLinkPayload }): SagaIterator {
  let { token, promId } = action.payload;

  yield put(setLoading(true));

  try {
    const response = yield call(axios.get, `/patients/${token}`);
    const result = response.data as ApiResponse;

    const joint = result.task.patients[0].parameters.find((param) => param.name === 'JOINT');
    const side = result.task.patients[0].parameters.find((param) => param.name === 'SIDE');
    const op = result.task.patients[0].parameters.find(
      (param) => param.name === 'PRE_POST_OPERATION'
    );
    const implementation_date = result.task.patients[0].parameters.find(
      (param) => param.name === 'OPERATION_DATE'
    );
    const taskCaseId = result.task.patients[0].parameters.find(
      (param) => param.name === 'TASK_CASE_ID'
    );
    const formTaskPatientId = result.task.patients[0].id;
    let proms = result.task.proms;
    if (promId) {
      proms = proms.filter((prom) => parseInt(prom.id, 10) === parseInt(promId as string, 10));
    }

    let mappedJoint = null;
    switch (joint?.value) {
      case '201':
        mappedJoint = 'knee';
        break;
      case '301':
      case '302':
        mappedJoint = 'shoulder';
        break;
      case '101':
        mappedJoint = 'hip';
        break;
      default:
        alert(i18n.t('common:login.errors.unknown_joint'));
        return;
    }

    let mappedSide = null;
    switch (side?.value) {
      case '1':
        mappedSide = 'right';
        break;
      case '2':
        mappedSide = 'left';
        break;
      default:
        alert(i18n.t('common:login.errors.unknown_side'));
        return;
    }

    // set mapped joint to patient
    result.patient.joint = mappedJoint;
    result.patient.op = op?.value === '0' ? 'pre' : 'post';
    if (implementation_date) {
      result.patient.implementation_date = implementation_date.value;
    }
    if (taskCaseId) {
      result.patient.taskCaseId = taskCaseId.value;
    }
    if (formTaskPatientId) {
      result.patient.formTaskPatientId = formTaskPatientId;
    }

    const tabs = proms.map((prom) => {
      const promName = prom.promName;
      let questionnaire = '';
      if (promName.includes('SO_MDS_2025')) {
        questionnaire = 'eq';
      } else if (promName.includes('SHOULDER')) {
        questionnaire = 'oss';
      } else if (promName.includes('KNEE')) {
        questionnaire = 'oks';
      } else if (promName.includes('HIP')) {
        questionnaire = 'ohs';
      }

      return {
        key: `single_${mappedSide}_x_${questionnaire}`,
        joint: mappedJoint,
        side: i18n.t(`common:sides.${mappedSide}`),
        skipOverview: true,
        type: questionnaire
      };
    });

    tabs.sort((a, b) => {
      const order: Record<string, number> = { eq: 0, oss: 1, oks: 1, ohs: 1 };
      return (order[a.type] ?? 99) - (order[b.type] ?? 99);
    });

    // define form from payload
    result.form = {
      activeTab: tabs[0].key,
      tabs
    };

    yield put(setLink(result));
    yield put(
      setLanguage(
        result.task.defaultLanguage && result.task.defaultLanguage !== '-1'
          ? result.task.defaultLanguage
          : 'de'
      )
    );
  } catch (error) {
    yield put(setError('Failed to load provided token'));
    console.error('Error loading patient info', error);
  } finally {
    yield put(setLoading(false));
  }
}

function* submitAnswers(): SagaIterator {
  console.log('Submitting answers');
  const form = yield select((state: QuestionnaireSelectorState) => state.app.form);
  const patient = yield select((state: QuestionnaireSelectorState) => state.app.patient);
  const task = yield select((state: QuestionnaireSelectorState) => state.app.task);
  const token = yield select((state: QuestionnaireSelectorState) => state.app.token);
  yield put(setLoading(true));
  yield put(push(`/questionnaire/submit`));

  const results = {
    success: [],
    failed: []
  } as {
    success: {
      questionKey: string;
      patient: Partial<Patient>;
      form: Partial<Form>;
    }[];
    failed: {
      questionKey: string;
      patient: Partial<Patient>;
      form: Partial<Form>;
      exception: unknown;
    }[];
  };

  try {
    const forms = [];
    for (const questionKey of Object.keys(form.answers)) {
      if (questionKey === 'overview') {
        continue;
      }

      const [m, , , questionnaireType] = questionKey.split('_');
      const answers = form.answers[questionKey] as Record<string, FormValue>;

      let isIncomplete = false;
      const questions = i18n.t(`${questionnaireType}:joints.${m}`, {
        returnObjects: true
      }) as Question[];
      const visibleQuestions = questionnaireService.prepareQuestions(-1, questions, {
        ...answers
      });
      isIncomplete = visibleQuestions.some((q: Question) => {
        if (q.type === 'hidden' || q.optional || q.key === 'FORM_NAME') return false;
        if (
          q.ignore &&
          questionnaireService.evaluateQuestion(q.ignore as string | boolean, answers)
        )
          return false;
        const value = answers[q.key];
        return (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        );
      });

      const mappedForm = questionnaireService.mapQuestions(
        patient,
        task,
        questionKey,
        answers,
        i18n.language,
        { incomplete: isIncomplete }
      );

      // Form with side, timestamp and PID
      forms.push({
        questionKey,
        patient,
        form: mappedForm
      });
    }

    for (const aForm of forms) {
      try {
        const [success, exception]: [boolean, Error] = yield call(
          (submission) => questionnaireService.processSubmission(submission, token),
          aForm
        );
        if (success) {
          results.success.push(aForm);
        } else {
          results.failed.push({ ...aForm, exception });
        }
      } catch (exception) {
        results.failed.push({ ...aForm, exception });
      }
    }

    console.log('total success', results.success.length);
    console.log('total error', results.failed.length);
    yield put(setError(results.failed.length > 0));
  } catch (e) {
    console.error('e', e);
    yield put(setError(true));
  } finally {
    yield put(setLoading(false));
  }
}

export const watcher = function* w(): SagaIterator {
  yield takeLatest<RequestAction>(requestLink.type, request);
  yield takeLatest<SubmitAnswersAction>(submitAnswersAction.type, submitAnswers);
};
